import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { CollectionArrow } from '@sorare/core/src/atoms/icons/CollectionArrow';
import { iconAlertCircle } from '@sorare/core/src/atoms/icons/system/iconAlertCircle.ts';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { rewardsOverviewToSimpleRewards } from '@sorare/core/src/lib/rewards';

import { DumbStatus } from '#src/components/managerTeam/Status/DumbStatus/index.tsx';
import { messages } from '#src/components/managerTeam/Status/messages.ts';
import { RewardOverview } from '#src/components/rewards/RewardOverview/index.tsx';
import { Variant } from '#src/components/rewards/RewardOverview/types.ts';
import { colorPerBonusRating, rgbColorPerBonusRating } from '#src/lib/bonus.ts';
import { ContenderStatus, getContenderStatus } from '#src/lib/contender.ts';
import { isFixtureOpened } from '#src/lib/so5.ts';

import type { LineupStatus_so5LeaderboardContender } from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  min-height: 78px;
  justify-content: space-between;
  padding: var(--unit) var(--double-unit);
  background-image: linear-gradient(
    0deg,
    rgba(var(--rgb-gradient-color), 0.2) 0%,
    transparent 100%
  );

  &:hover {
    background-image: linear-gradient(
      0deg,
      rgba(var(--rgb-gradient-color), 0.25) 0%,
      transparent 100%
    );
  }

  &.isUpcoming {
    min-height: 48px;
  }
  > *:only-child {
    margin: auto 0;
  }
`;

const Sup = styled.sup`
  font-size: var(--fs-10);
  text-transform: uppercase;
  color: var(--c-nd-600);
`;

const divisionChangeColor: Record<DivisionChange, string> = {
  [DivisionChange.PROMOTED]: 'var(--c-rgb-onBase-success)',
  [DivisionChange.RELEGATED]: '255, 78, 34',
  [DivisionChange.STABLE]: '69, 69, 69',
};

type Props = {
  so5LeaderboardContender?: LineupStatus_so5LeaderboardContender;
  message?: ReactNode;
  children?: ReactNode;
  className?: string;
  onClickPrizePool?: () => void;
  onClickBonus?: () => void;
};

export const LineupStatus = ({
  so5LeaderboardContender,
  message: messageProp,
  className,
  onClickPrizePool,
  onClickBonus,
  children,
}: Props) => {
  const isUpcoming =
    so5LeaderboardContender &&
    isFixtureOpened(so5LeaderboardContender.so5Leaderboard.so5Fixture);
  if (!so5LeaderboardContender) {
    return <Root className={classNames(className, { isUpcoming })} />;
  }

  const { so5Leaderboard, so5Lineup } = so5LeaderboardContender;

  const contenderStatus = getContenderStatus(so5LeaderboardContender);
  const { status } = contenderStatus;

  let content: ReactNode = null;
  let icon: ReactNode = null;
  let message: ReactNode = null;
  let color: string = 'var(--c-rgb-black)';
  let onClick: (() => void) | undefined;

  if (status === ContenderStatus.EMPTY) {
    const simpleRewards = rewardsOverviewToSimpleRewards({
      rewardsOverview: so5Leaderboard.totalRewards,
      rarity: so5Leaderboard.mainRarityType,
    });

    color = '42, 42, 42';
    content = (
      <SBHorizontal>
        <BodyM bold>
          <FormattedMessage {...fantasy.viewPrizePool} />
        </BodyM>
        <Horizontal>
          {simpleRewards.map((simpleReward, i) => (
            <RewardOverview
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              size={3}
              variant={Variant.IMAGE_ONLY}
              simpleReward={simpleReward}
            />
          ))}
        </Horizontal>
      </SBHorizontal>
    );
    onClick = onClickPrizePool;
  } else if (status === ContenderStatus.INCOMPLETE) {
    icon = (
      <FontAwesomeIcon icon={iconAlertCircle} color="var(--c-yellow-400)" />
    );
    message = <FormattedMessage {...fantasy.incomplete} />;
    color = '240, 206, 29';
  } else if (status === ContenderStatus.PROJECTED_DIVISION) {
    color = divisionChangeColor[contenderStatus.divisionChange];
    icon = <DumbStatus divisionChange={contenderStatus.divisionChange} />;

    const projectedDivisionDelta =
      contenderStatus.divisionChange === DivisionChange.PROMOTED ? -1 : +1;
    const projectedDivision =
      (so5Leaderboard.division || 0) +
      (contenderStatus.divisionChange === DivisionChange.STABLE
        ? 0
        : projectedDivisionDelta);

    message = (
      <FormattedMessage
        {...messages.projectedToDiv}
        values={{ division: projectedDivision }}
      />
    );
    onClick = onClickPrizePool;
  } else if (status === ContenderStatus.VIEW_BONUS && so5Lineup?.bonusRating) {
    color = rgbColorPerBonusRating[so5Lineup.bonusRating];
    icon = (
      <CollectionArrow color={colorPerBonusRating[so5Lineup.bonusRating]} />
    );
    message = <FormattedMessage defaultMessage="View Bonus" />;
    onClick = onClickBonus;
  } else if (status === ContenderStatus.BONUS && so5Lineup?.bonusRating) {
    color = '0, 0, 0';
    icon = (
      <CollectionArrow color={colorPerBonusRating[so5Lineup.bonusRating]} />
    );
    message = (
      <Horizontal gap={0.5}>
        <FormattedMessage
          {...fantasy.ordinalPoints}
          values={{
            score: so5Lineup.bonusPoints ? (
              <FormattedNumber
                value={so5Lineup.bonusPoints}
                maximumFractionDigits={0}
                signDisplay="always"
              />
            ) : (
              '-'
            ),
            sup: c => <Sup>{c}</Sup>,
          }}
        />
      </Horizontal>
    );
    onClick = onClickBonus;
  }

  return (
    <Root
      style={
        {
          '--rgb-gradient-color': color,
        } as CSSProperties
      }
      className={classNames(className, { isUpcoming })}
    >
      {children}
      {!!(content || message || messageProp) && (
        <ConditionalWrapper
          wrap={!!onClick}
          Wrapper={ButtonBase}
          props={{ onClick }}
        >
          {content || (
            <Vertical center gap={0.5}>
              <Horizontal center>
                {icon}
                <BodyM bold>{messageProp || message}</BodyM>
              </Horizontal>
            </Vertical>
          )}
        </ConditionalWrapper>
      )}
    </Root>
  );
};

LineupStatus.fragments = {
  so5LeaderboardContender: gql`
    fragment LineupStatus_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        id # used to check if the query is fully loaded
        slug
        division
        mainRarityType
        totalRewards {
          ...rewardsOverviewToSimpleRewards_rewardsOverview
        }
        rewardsConfig {
          conditional {
            id
            score
          }
        }
        so5Fixture {
          slug
          ...isFixtureOpened_so5Fixture
        }
      }
      so5Lineup {
        id
        bonusRating
        bonusPoints
        so5Appearances {
          id
          upcomingGame {
            id
          }
        }
        so5Rankings {
          id
          score
          targetedRewardWithEligibility {
            eligible
            reward {
              id
              score
              target {
                slug
                pictureUrl
                color
              }
            }
          }
        }
      }
      ...getContenderStatus_so5LeaderboardContender
    }
    ${getContenderStatus.fragments.so5LeaderboardContender}
    ${isFixtureOpened.fragments.so5Fixture}
    ${rewardsOverviewToSimpleRewards.fragments.rewardsOverview}
  ` as TypedDocumentNode<LineupStatus_so5LeaderboardContender>,
};
